import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { LintFinding } from '../videoLinter';

import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * Error: a text asset's rendered text overflows its `maxWidth × maxHeight`
 * bounds, so part of it is clipped off at the box edge and never shown. This is
 * the same condition the creator surfaces with a persistent clip indicator next
 * to the asset.
 *
 * `TextAsset.isClipping` is only meaningful when `enableEnhancedAssetTransform`
 * is enabled on the Luminary instance — it is hard-coded to `false` otherwise.
 * When the instance doesn't have it enabled we skip the check entirely rather
 * than report a clean pass, which would be misleading.
 */
export function checkTextClipping(luminary: Luminary, _getState: GetRootState): LintFinding[] {
    if (!luminary.enableEnhancedAssetTransform) {
        return [];
    }

    const findings: LintFinding[] = [];

    luminary.scenes.forEach((scene) => {
        const textAssets = scene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter((asset) => asset.isEditable && asset.text?.trim());

        textAssets.forEach((asset) => {
            if (!asset.isClipping) {
                return;
            }
            const text = asset.text.trim();
            findings.push({
                check: 'text_clipping',
                severity: 'error',
                sceneId: scene.id,
                message: `Text "${text}" is clipped at its box's edge, so part of it is cut off. Resize the box, lower the font size, shorten the text, or swap the scene to a design that accommodates more text so all of it shows.`,
            });
        });
    });

    return findings;
}
